import { useEffect, useRef, useState, useCallback } from "react";
import useLanguage from "../common_hooks/useLanguage";
import { useSelector } from "react-redux";

export default function useDrawInstruction() {
  const { mode_toolbox } = useSelector((state) => state.toolbox);
  const { map_object, draw_object } = useSelector((state) => state.layer);
  const { dict, language } = useLanguage();

  const lastLengthRef = useRef(null);
  const lastModeRef = useRef("");
  const lastInstructionRef = useRef("");

  const rafRef = useRef(null);
  const scheduledRef = useRef(false);

  const [instruction, setInstruction] = useState("");

  const computeInstruction = useCallback(() => {
    scheduledRef.current = false;

    if (!draw_object) return;

    const mode = draw_object.getMode();
    const features = draw_object?.getAll()?.features || [];
    const feature = features && features[features.length - 1];

    if (!mode_toolbox) {
      setInstruction("");
      return;
    }

    if (!feature) {
      if (lastInstructionRef.current !== "") {
        lastInstructionRef.current = "";
        setInstruction("");
      }
      lastLengthRef.current = null;
      lastModeRef.current = mode;
      return;
    }

    let coordLength = 0;

    if (feature.geometry.type === "LineString") {
      coordLength = feature.geometry.coordinates.length;
    }

    if (feature.geometry.type === "Polygon") {
      coordLength = feature.geometry.coordinates[0].length;
    }

    // 🛑 GUARD PALING PENTING
    if (
      lastLengthRef.current === coordLength &&
      lastModeRef.current === mode
    ) {
      return;
    }

    lastLengthRef.current = coordLength;
    lastModeRef.current = mode;

    let next = "";

    if (mode === "draw_line_string" || mode === "draw_polygon") {
      next = coordLength <= 1
        ? dict["Click on the map to choose your start location."][language]
        : dict["Click for another point, then double-click to finish."][language];
    }

    if (mode === "draw_point") {
      next = mode_toolbox === "features_radius"
        ? dict["Click to create a radius."][language]
        : next = dict["Click on the map to place a point."][language];
    }

    if (next !== lastInstructionRef.current) {
      lastInstructionRef.current = next;
      setInstruction(next);
    }
  }, [draw_object, dict, language, mode_toolbox]);

  useEffect(() => {
    if (!map_object || !draw_object) return;

    const scheduleUpdate = () => {
      if (scheduledRef.current) return;

      scheduledRef.current = true;
      rafRef.current = requestAnimationFrame(computeInstruction);
    };

    map_object.on("draw.render", scheduleUpdate);
    map_object.on("draw.modechange", scheduleUpdate);

    // initial sync
    scheduleUpdate();

    return () => {
      map_object.off("draw.render", scheduleUpdate);
      map_object.off("draw.modechange", scheduleUpdate);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [map_object, draw_object, mode_toolbox, computeInstruction]);

  return instruction;
}

